import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Flame, Award, X, Share2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { toast } from "sonner";

const MILESTONES = { 4: "One Month Strong", 12: "Quarterly Champion", 52: "Wealth Marathoner" };

export default function StreakWidget() {
  const [data, setData] = useState(null);
  const [celebrating, setCelebrating] = useState(null);
  const shotRef = useRef(false);

  useEffect(() => { api.get("/streaks").then(r => setData(r.data)).catch((e) => console.error("streaks:", e?.message || e)); }, []);

  useEffect(() => {
    if (!data || shotRef.current) return;
    const current = data.current_streak;
    const key = "arm_last_milestone";
    const lastCelebrated = parseInt(localStorage.getItem(key) || "0", 10);
    const target = [52, 12, 4].find(n => current >= n && n > lastCelebrated);
    if (target) {
      shotRef.current = true;
      localStorage.setItem(key, String(target));
      setCelebrating(target);
      // Fire confetti
      const end = Date.now() + 2500;
      const colors = ["#D4AF37", "#E9C46A", "#F3E5AB", "#E06A3B"];
      (function frame() {
        confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
        confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
      })();
    }
  }, [data]);

  const shareBadge = async () => {
    const label = MILESTONES[celebrating];
    const text = `Just hit ${celebrating} weeks straight on AfroRichMind — ${label}. Personalized to your actual money.`;
    const url = window.location.origin;
    if (navigator.share) {
      try { await navigator.share({ title: "AfroRichMind", text, url }); }
      catch {}
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Badge text copied to share");
    }
  };

  if (!data) return null;
  return (
    <>
      <div data-testid="streak-widget" className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-[color:var(--terracotta)]" />
            <h3 className="font-heading text-lg font-semibold">Weekly Streak</h3>
          </div>
          <div className="text-xs text-[color:var(--text-muted)]">Longest: {data.longest_streak}w</div>
        </div>
        <div className="flex items-baseline gap-2 mb-4">
          <span data-testid="current-streak" className="font-mono text-5xl font-bold gold-gradient-text">{data.current_streak}</span>
          <span className="text-sm text-[color:var(--text-secondary)]">week{data.current_streak === 1 ? "" : "s"} in a row</span>
        </div>
        <div className="flex gap-1 mb-4">
          {data.weeks.map((w, i) => (
            <motion.div key={i} initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.03 }}
              title={`${w.week}: ${w.hit ? "Hit" : "Miss"} · saved $${w.saved}`}
              className={`flex-1 h-8 rounded-md ${w.hit ? "bg-gradient-to-b from-[#D4AF37] to-[#B38728]" : "bg-[rgba(255,255,255,0.04)] border border-[rgba(212,175,55,0.1)]"}`}
            />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {data.milestones.map((m) => (
            <div key={m.weeks} data-testid={`milestone-${m.weeks}`}
              className={`text-center p-2 rounded-lg border transition ${m.earned ? "border-[rgba(212,175,55,0.4)] bg-[rgba(212,175,55,0.08)]" : "border-[rgba(212,175,55,0.1)] opacity-40"}`}>
              <Award className={`w-4 h-4 mx-auto mb-1 ${m.earned ? "text-[color:var(--gold)]" : "text-[color:var(--text-muted)]"}`} />
              <div className="text-[10px] font-mono">{m.weeks}w</div>
              <div className="text-[9px] leading-tight text-[color:var(--text-secondary)]">{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {celebrating && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xl flex items-center justify-center p-4"
            onClick={() => setCelebrating(null)}>
            <motion.div initial={{ scale: 0.5, rotate: -10 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0.5, opacity: 0 }}
              transition={{ type: "spring", damping: 15 }}
              data-testid="milestone-celebration"
              className="glass-card p-10 max-w-md text-center border-[rgba(212,175,55,0.5)] relative" onClick={e => e.stopPropagation()}>
              <button onClick={() => setCelebrating(null)} className="absolute top-4 right-4 text-[color:var(--text-muted)] hover:text-white"><X className="w-5 h-5" /></button>
              <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#D4AF37] via-[#F3E5AB] to-[#B38728] flex items-center justify-center mb-6 pulse-glow">
                <Award className="w-12 h-12 text-black" />
              </div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--gold)] mb-2">Milestone unlocked</div>
              <h2 className="font-heading text-3xl font-bold gold-gradient-text mb-2">{MILESTONES[celebrating]}</h2>
              <p className="text-[color:var(--text-secondary)] mb-6">{celebrating} weeks straight of hitting your money goals. That's rare air.</p>
              <button data-testid="share-badge-btn" onClick={shareBadge} className="gold-btn px-6 py-3 rounded-full inline-flex items-center gap-2">
                <Share2 className="w-4 h-4" /> Share the win
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
